import { memo, useMemo } from "react";
import { createLogger } from "../../../helper/logger";
import DropdownCellRenderer from "../../../shared/ag-grid/cellrenderer/dropdownCellRenderer";
import ImageCellRenderer from "../../../shared/ag-grid/cellrenderer/imageCellRenderer";
import InputBoxCellRenderer from "../../../shared/ag-grid/cellrenderer/inputBoxCellRenderer";
import KeywordPositionsCellRenderer from "../../../shared/ag-grid/cellrenderer/keywordPositionsCellRenderer";
import FRPAgGrid from "../../../shared/ag-grid/frp-ag-grid";

/**
 * The column definitions for the output review grid
 */

const permanentStatusOptions = [
	{ label: "Select", Id: 0 },
	{ label: "Issue", Id: 1 },
	{ label: "Non-Issue", Id: 2 },
	{ label: "Ignore", Id: 3 },
];

const confirmResultOptions = [
	{ label: "Issue", Id: 0 },
	{ label: "Non-Issue", Id: 1 },
];

export const columnDefs = [
	{
		headerName: "SN",
		field: "sn",
		filter: "agNumberColumnFilter",
		width: 70,
		minWidth: 70,
		maxWidth: 70,
	},
	{
		headerName: "Document Name",
		field: "documentName",
		filter: "agTextColumnFilter",
		width: 120,
		minWidth: 120,
		maxWidth: 200,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
		tooltipField: "documentName",
	},
	{
		headerName: "Line of Business",
		field: "lineOfBusiness",
		filter: "agTextColumnFilter",
		width: 120,
		minWidth: 120,
		maxWidth: 150,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
	},
	{
		headerName: "Issue Name",
		field: "issueName",
		filter: "agTextColumnFilter",
		width: 100,
		minWidth: 100,
		maxWidth: 160,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
		tooltipField: "issueName",
	},
	{
		headerName: "Result",
		field: "result",
		filter: "agTextColumnFilter",
		width: 90,
		minWidth: 90,
		maxWidth: 120,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
	},
	{
		headerName: "Confirm Result",
		field: "confirmResult",
		filter: "agTextColumnFilter",
		width: 100,
		minWidth: 100,
		maxWidth: 150,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
		cellRenderer: DropdownCellRenderer,
		cellRendererParams: {
			options: confirmResultOptions,
		},
	},
	{
		headerName: "Issue Type",
		field: "issueType",
		filter: "agTextColumnFilter",
		width: 90,
		minWidth: 90,
		maxWidth: 120,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
	},
	{
		headerName: "Page Number",
		field: "pageNumber",
		filter: "agTextColumnFilter",
		width: 100,
		minWidth: 100,
		maxWidth: 140,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
	},
	{
		headerName: "Form Number",
		field: "formNumber",
		filter: "agTextColumnFilter",
		width: 100,
		minWidth: 100,
		maxWidth: 170,
		autoHeight: true,
		wrapHeaderText: true,
		cellRenderer: InputBoxCellRenderer,
		cellRendererParams: {
			placeholder: "Enter Form Number",
			maxLength: 50,
		},
		flex: 1,
	},
	{
		headerName: "Permanent Status",
		field: "permanentStatus",
		filter: "agTextColumnFilter",
		width: 120,
		minWidth: 120,
		maxWidth: 160,
		autoHeight: true,
		wrapHeaderText: true,
		cellRenderer: DropdownCellRenderer,
		cellRendererParams: {
			options: permanentStatusOptions,
		},
		flex: 1,
	},
	{
		headerName: "Rule Keyword",
		field: "ruleKeyword",
		width: 100,
		minWidth: 100,
		maxWidth: 200,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
		cellRenderer: KeywordPositionsCellRenderer,
	},
	{
		headerName: "Policy Page Screenshot",
		field: "policyPageScreenshot",
		width: 800,
		minWidth: 800,
		maxWidth: 1000,
		autoHeight: true,
		wrapHeaderText: true,
		flex: 1,
		cellRenderer: ImageCellRenderer,
	},
];

/**
 * Grid component for output review data
 */
const OutputReviewGrid = ({
	gridRef,
	rowData,
	onRowDataUpdated,
	datasource,
	getRowId,
}) => {
	const logger = useMemo(() => createLogger("OutputReviewGrid"), []);

	// Log what data model we're using
	if (datasource) {
		logger.debug("Rendering grid with datasource for infinite scrolling");
	} else {
		logger.debug("Rendering grid with rowData", rowData);
		logger.debug("rowData length", rowData?.length || 0);
		logger.debug("First few items", rowData?.slice(0, 3));
	}

	// Configure infinite scrolling props
	const infiniteProps = datasource
		? {
				cacheBlockSize: 50,
				maxBlocksInCache: 10,
				infiniteInitialRowCount: 1,
				maxConcurrentDatasourceRequests: 2,
				datasource,
				getRowId,
			}
		: {};

	return (
		<div className={"ag-theme-quartz"} style={{ width: "100%", height: 655 }}>
			<FRPAgGrid
				gridRef={gridRef}
				columnDefs={columnDefs}
				rowData={!datasource ? rowData : undefined}
				pagination={false}
				{...infiniteProps}
				onRowDataUpdated={(event) => {
					logger.debug("Row data updated event", event);
					if (onRowDataUpdated) {
						onRowDataUpdated(event);
					}
				}}
			/>
		</div>
	);
};

export default memo(OutputReviewGrid);
